import React, { FC, useEffect, useState } from 'react';
import { ArchivePageTableProps } from './ArchivePageTableProps';
import { Stack } from '@mui/material';
import {
  setupColumnsWidthAndOrder,
  useDatagridSettings,
} from '../../hooks/useDatagridSettings';
import useTranslation from '../../hooks/useTranslation';
import {
  DEFAULT_VIEW_COLUMNS,
  FORECAST_VIEW_COLUMNS,
  returnArchiveTableColumns,
} from './utils';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { CustomToolbar } from '../ProjectListTable/toolbar/CustomToolbar';
import Button from '@ingka/button';
import trashcan from '@ingka/ssr-icon/paths/trash-can';
import arrowBack from '@ingka/ssr-icon/paths/arrow-counterclockwise';
import { ProjectStatus } from '../../models/project';
import skapaColours from '@ingka/variables/colours-css';
import { FINANCIAL_VIEW_COLUMNS } from '../ApprovalPageTable/utils';
import { filterLadaProjects } from '../ProjectListTable/utils';

const ArchivePageTable: FC<ArchivePageTableProps> = ({
  projects,
  loading,
  tableSelection,
  setTableSelection,
  deleteProject,
  unArchiveProject,
  onRowClick,
}) => {
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0,
  });
  const t = useTranslation();
  const settings = useDatagridSettings('archiveList');

  const [visibleProjects, setVisibleProjects] = useState(projects);

  const columns = returnArchiveTableColumns(t);

  useEffect(() => {
    setVisibleProjects(projects);
  }, [projects]);

  const viewClicked = (view) => {
    const visibilityObject = {};
    let columnsToShow = [];
    let columnsToHide;
    switch (view) {
      case 'default':
        columnsToShow = DEFAULT_VIEW_COLUMNS;
        columnsToHide = [...FORECAST_VIEW_COLUMNS];
        setVisibleProjects(projects);
        break;
      case 'lada':
        columnsToShow = DEFAULT_VIEW_COLUMNS;
        columnsToHide = [...FORECAST_VIEW_COLUMNS, ...FINANCIAL_VIEW_COLUMNS];
        const ladaProjects = filterLadaProjects(projects);
        setVisibleProjects(ladaProjects);
        break;
      case 'forecast':
      default:
        columnsToShow = FORECAST_VIEW_COLUMNS;
        columnsToHide = [...DEFAULT_VIEW_COLUMNS];

        const filteredProjects = projects.filter((project) => {
          const info = project.financialSummaryInfos?.[0];
          return (
            info !== null &&
            info?.totalInvestment > 0 &&
            project.unitCategory !== 'meetingPlace' &&
            ![ProjectStatus.CANCELLED, ProjectStatus.ON_HOLD].includes(
              project.projectStatus as ProjectStatus,
            )
          );
        });

        setVisibleProjects(filteredProjects);
    }
    columnsToHide.forEach((column) => {
      visibilityObject[column] = false;
    });
    columnsToShow.forEach((column) => {
      visibilityObject[column] = true;
    });
    settings.datagridProps.onColumnVisibilityModelChange(visibilityObject);
  };

  return (
    <Stack
      position="relative"
      p={2}
      height="100%"
      data-testid="ppm-archive-list"
    >
      <Stack position="absolute" sx={{ inset: 0 }}>
        <DataGridPremium
          {...settings.datagridProps}
          initialState={{
            sorting: {
              sortModel: [{ field: 'lastUpdatedDate', sort: 'desc' }],
            },
          }}
          rowHeight={64}
          sx={{
            border: 0,
            '.Mui-DatagridRow-even': {
              backgroundColor: skapaColours.colourNeutral2,
            },
            '.MuiDataGrid-columnHeaderTitle': {
              color: skapaColours.colourNeutral6,
            },
          }}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0
              ? 'Mui-DatagridRow-even'
              : 'Mui-DatagridRow-odd'
          }
          slots={{ toolbar: CustomToolbar }}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          onRowSelectionModelChange={setTableSelection}
          rowSelectionModel={tableSelection}
          checkboxSelection
          disableRowSelectionOnClick
          loading={loading}
          pageSizeOptions={[10, 25, 50, 100]}
          slotProps={{
            toolbar: {
              handleStorage: settings.saveSettings,
              views: [
                { label: 'Default view', value: 'default' },
                { label: 'Next FY Forecast', value: 'forecast' },
                { label: 'Lada', value: 'lada' },
              ],
              onViewClick: (view) => {
                viewClicked(view);
              },
              handleReset: settings.resetSettings,
              controls: tableSelection.length ? (
                <>
                  <Button
                    type="secondary"
                    iconOnly
                    ssrIcon={trashcan}
                    size="small"
                    onClick={deleteProject}
                  />
                  <Button
                    type="primary"
                    size="small"
                    ssrIcon={arrowBack}
                    onClick={unArchiveProject}
                  >
                    {t('Revert from archive')}
                  </Button>
                </>
              ) : undefined,
            },
          }}
          pagination
          onRowClick={(params) => {
            if (params?.id?.toString().includes('auto-generated-row')) return;
            if (onRowClick) onRowClick(params?.row?.id as string);
          }}
          columns={setupColumnsWidthAndOrder(
            columns,
            settings.datagridProps.columnSizeModel,
            settings.datagridProps.columnOrderModel,
          )}
          rows={visibleProjects}
        />
      </Stack>
    </Stack>
  );
};

export default ArchivePageTable;
